import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import {
  acceptAbsenceRequests,
  cancelAbsenceRequest,
  createAbsenceRequest,
  editAbsenceRequest,
  fetchAbsenceRequestHistory,
  fetchEmployeeAbsenceRequest,
  fetchEmployeeAbsenceRequests,
  fetchManagedAbsenceRequests,
  rejectAbsenceRequests,
  requireCorrectionAbsenceRequest,
  ResponseTypeList,
} from "src/Pages/Company/Absences/api";
import {
  IAbsenceRequest,
  IAbsenceRequestDTO,
  IAbsenceRequestHistory,
  IEmployeeAbsenceRequestListItem,
  IRequireCorrectionDTO,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { useHistory } from "react-router-dom";
import useBasePath from "src/utils/hooks/useBasePath";

export const useFetchEmployeeAbsenceRequests = (params: {
  employeeId: string;
  page: number;
  size: number;
  sort?: string[] | undefined;
}) =>
  useQuery<ResponseTypeList<IEmployeeAbsenceRequestListItem>, Error>({
    queryKey: ["employee-absence-requests", params],
    queryFn: ({ signal }) =>
      fetchEmployeeAbsenceRequests({
        ...params,
        signal,
      }),
  });

export const useFetchManagedAbsenceRequests = (params: {
  page: number;
  size: number;
  sort?: string[] | undefined;
}) => {
  const companyId = useCompanyId();

  return useQuery<ResponseTypeList<IEmployeeAbsenceRequestListItem>, Error>({
    queryKey: ["managed-absence-requests", params],
    queryFn: ({ signal }) =>
      fetchManagedAbsenceRequests({
        companyId,
        ...params,
        signal,
      }),
  });
};

export const useFetchEmployeeAbsenceRequest = (absenceRequestId: string) =>
  useQuery<IAbsenceRequest, Error>({
    queryKey: ["employee-absence-request", { absenceRequestId }],
    queryFn: ({ signal }) =>
      fetchEmployeeAbsenceRequest(absenceRequestId, signal),
  });

export const useFetchAbsenceRequestHistory = (absenceRequestId: string) =>
  useQuery<IAbsenceRequestHistory>({
    queryKey: ["absence-request-history", { absenceRequestId }],
    queryFn: ({ signal }) =>
      fetchAbsenceRequestHistory(absenceRequestId, signal),
    enabled: !!absenceRequestId,
  });

export const useMutateAbsenceRequest = (
  employeeId: string,
  absenceRequestId?: string
) => {
  const history = useHistory();
  const basePath = useBasePath();
  const queryClient = useQueryClient();

  const mutation = useMutation<
    IAbsenceRequestDTO,
    AxiosError,
    IAbsenceRequestDTO
  >({
    mutationFn: async (data) =>
      absenceRequestId
        ? editAbsenceRequest(absenceRequestId, data)
        : createAbsenceRequest(data),
    onSuccess: () => {
      const message = absenceRequestId
        ? "Zmiany we wniosku zostały poprawnie zapisane."
        : "Wniosek został pomyślnie złożony.";

      queryClient.invalidateQueries({
        queryKey: ["employee-absence-request"],
      });

      dynamicNotification(__(message), "success");
      // A bit of a hack to ensure all state updates are done before redirecting
      // Used for confirmation modal
      setTimeout(() => {
        history.push(`${basePath}/absences/employee/${employeeId}#requests`);
      }, 500);
    },
    onError: () => {
      const message = absenceRequestId
        ? "Wystąpił błąd. Nie udało się zapisać zmian we wniosku."
        : "Wystąpił błąd. Nie udało się złożyć wniosku.";

      dynamicNotification(__(message), "error", "top-right", 7000);
    },
  });

  return mutation;
};

export const useAcceptAbsenceRequests = (resetModal?: VoidFunction) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (absenceRequests: string[]) =>
      acceptAbsenceRequests(absenceRequests),
    onSuccess: () => {
      dynamicNotification(
        __("Wniosek został pomyślnie zaakceptowany."),
        "success"
      );
      queryClient.invalidateQueries({
        queryKey: ["employee-absence-requests"],
      });
      queryClient.invalidateQueries({
        queryKey: ["managed-absence-requests"],
      });

      if (typeof resetModal === "function") resetModal();
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd. Nie udało się zaakceptować wniosku."),
        "error",
        "top-right",
        7000
      );
    },
  });

  return mutation;
};

export const useRejectAbsenceRequests = (resetModal: VoidFunction) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (absenceRequests: string[]) =>
      rejectAbsenceRequests(absenceRequests),
    onSuccess: () => {
      dynamicNotification(__("Wniosek został pomyślnie odrzucony."), "success");
      queryClient.invalidateQueries({
        queryKey: ["employee-absence-requests"],
      });
      queryClient.invalidateQueries({
        queryKey: ["managed-absence-requests"],
      });

      resetModal();
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd. Nie udało się odrzucić wniosku."),
        "error",
        "top-right",
        7000
      );
    },
  });

  return mutation;
};

export const useRequireCorrectionAbsenceRequest = (
  absenceRequestId: string,
  resetModal: VoidFunction
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    IRequireCorrectionDTO,
    AxiosError,
    IRequireCorrectionDTO
  >({
    mutationFn: async (data) =>
      requireCorrectionAbsenceRequest(absenceRequestId, data),
    onSuccess: () => {
      dynamicNotification(
        __("Wniosek został pomyślnie przekazany do poprawy."),
        "success"
      );
      queryClient.invalidateQueries({
        queryKey: ["employee-absence-requests"],
      });
      queryClient.invalidateQueries({
        queryKey: ["managed-absence-requests"],
      });

      resetModal();
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd. Nie udało się przekazać do poprawy wniosku."),
        "error",
        "top-right",
        7000
      );
    },
  });

  return mutation;
};

export const useCancelAbsenceRequests = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (absenceRequestId: string) =>
      cancelAbsenceRequest(absenceRequestId),
    onSuccess: () => {
      dynamicNotification(__("Wniosek został pomyślnie anulowany."), "success");
      queryClient.invalidateQueries({
        queryKey: ["employee-absence-requests"],
      });
      queryClient.invalidateQueries({
        queryKey: ["managed-absence-requests"],
      });
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd. Nie udało się anulować wniosku."),
        "error",
        "top-right",
        7000
      );
    },
  });

  return mutation;
};
